type IsNullOperatorType = 'is_null' | 'is_not_null'

type DimensionOperatorType =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'not_starts_with'
  | 'ends_with'
  | 'not_ends_with'
  | 'matches_regex'
  | 'not_matches_regex'
  | IsNullOperatorType

export type OptionOperatorType = 'is' | 'not_is' | 'one_of' | 'not_one_of' | IsNullOperatorType

export type MetricOperatorType = 'eq' | 'gt' | 'lt' | 'gte' | 'lte'

/** Saved dashboards and URLs may still use symbolic comparison operators. */
export const LEGACY_SYMBOLIC_METRIC_OPERATOR_TO_ID = {
  '=': 'eq',
  '>': 'gt',
  '<': 'lt',
  '>=': 'gte',
  '<=': 'lte'
} as const satisfies Record<string, MetricOperatorType>

export const normalizeMetricOperator = (operator: string): string =>
  LEGACY_SYMBOLIC_METRIC_OPERATOR_TO_ID[operator as keyof typeof LEGACY_SYMBOLIC_METRIC_OPERATOR_TO_ID] ?? operator

export const resolveStoredFilterOperator = (operator: string, allowedOperatorIds: Record<string, unknown>): string => {
  const normalized = normalizeMetricOperator(operator)
  if (normalized in allowedOperatorIds) {
    return normalized
  }
  return operator
}

export type OperatorType = DimensionOperatorType | OptionOperatorType | MetricOperatorType

export type DimensionType = 'source_medium' | 'source' | 'medium' | 'campaign' | 'hostname'

export type ConditionIDType = OperatorType | DimensionType
interface Conditions<T extends ConditionIDType = OperatorType | DimensionType> {
  /**
   * The ID of the operator used
   */
  id: T
  /**
   * The label or descriptive name for the operator ID
   */
  label: string
}

export const METRIC_OPERATORS: Record<MetricOperatorType, Conditions<MetricOperatorType>> = {
  eq: {
    id: 'eq',
    label: '='
  },
  gt: {
    id: 'gt',
    label: '>'
  },
  lt: {
    id: 'lt',
    label: '<'
  },
  gte: {
    id: 'gte',
    label: '>='
  },
  lte: {
    id: 'lte',
    label: '<='
  }
}

export const OPTION_OPERATORS: Record<OptionOperatorType, Conditions<OptionOperatorType>> = {
  is: {
    id: 'is',
    label: 'Is'
  },
  not_is: {
    id: 'not_is',
    label: 'Is not'
  },
  one_of: {
    id: 'one_of',
    label: 'Is one of'
  },
  not_one_of: {
    id: 'not_one_of',
    label: 'Is not one of'
  },
  is_null: {
    id: 'is_null',
    label: 'Is null'
  },
  is_not_null: {
    id: 'is_not_null',
    label: 'Is not null'
  }
}

export const DIMENSION_OPERATORS: Record<DimensionOperatorType, Conditions> = {
  equals: {
    id: 'equals',
    label: 'Exactly matches'
  },
  not_equals: {
    id: 'not_equals',
    label: 'Does not exactly match'
  },
  contains: {
    id: 'contains',
    label: 'Contains'
  },
  not_contains: {
    id: 'not_contains',
    label: 'Does not contain'
  },
  starts_with: {
    id: 'starts_with',
    label: 'Starts with'
  },
  not_starts_with: {
    id: 'not_starts_with',
    label: 'Does not start with'
  },
  ends_with: {
    id: 'ends_with',
    label: 'Ends with'
  },
  not_ends_with: {
    id: 'not_ends_with',
    label: 'Does not end with'
  },
  matches_regex: {
    id: 'matches_regex',
    label: 'Matches regex'
  },
  not_matches_regex: {
    id: 'not_matches_regex',
    label: 'Does not match regex'
  },
  is_null: {
    id: 'is_null',
    label: 'Is null'
  },
  is_not_null: {
    id: 'is_not_null',
    label: 'Is not null'
  }
}

type OperatorInputType = 'string' | 'single_select' | 'multi_select' | 'number' | null
export interface OperatorWithInputType<IDType extends string = string> {
  id: IDType
  label: string
  type: OperatorInputType
  unit?: string
}

export type DimensionAndOptionOperatorType = DimensionOperatorType | OptionOperatorType

export const METRIC_OPERATORS_WITH_TYPES: Record<MetricOperatorType, OperatorWithInputType<MetricOperatorType>> = {
  eq: {
    ...METRIC_OPERATORS.eq,
    type: 'number'
  },
  gt: {
    ...METRIC_OPERATORS.gt,
    type: 'number'
  },
  lt: {
    ...METRIC_OPERATORS.lt,
    type: 'number'
  },
  gte: {
    ...METRIC_OPERATORS.gte,
    type: 'number'
  },
  lte: {
    ...METRIC_OPERATORS.lte,
    type: 'number'
  }
} as const

export const OPTION_OPERATORS_WITH_TYPES: Record<OptionOperatorType, OperatorWithInputType<OptionOperatorType>> = {
  is: {
    ...OPTION_OPERATORS.is,
    type: 'single_select'
  },
  not_is: {
    ...OPTION_OPERATORS.not_is,
    type: 'single_select'
  },
  one_of: {
    ...OPTION_OPERATORS.one_of,
    type: 'multi_select'
  },
  not_one_of: {
    ...OPTION_OPERATORS.not_one_of,
    type: 'multi_select'
  },
  is_null: {
    ...OPTION_OPERATORS.is_null,
    type: null
  },
  is_not_null: {
    ...OPTION_OPERATORS.is_not_null,
    type: null
  }
} as const

export const DIMENSION_OPERATORS_WITH_TYPES: Record<DimensionAndOptionOperatorType, OperatorWithInputType> = {
  equals: {
    ...DIMENSION_OPERATORS.equals,
    type: 'string'
  },
  not_equals: {
    ...DIMENSION_OPERATORS.not_equals,
    type: 'string'
  },
  contains: {
    ...DIMENSION_OPERATORS.contains,
    type: 'string'
  },
  not_contains: {
    ...DIMENSION_OPERATORS.not_contains,
    type: 'string'
  },
  starts_with: {
    ...DIMENSION_OPERATORS.starts_with,
    type: 'string'
  },
  not_starts_with: {
    ...DIMENSION_OPERATORS.not_starts_with,
    type: 'string'
  },
  ends_with: {
    ...DIMENSION_OPERATORS.ends_with,
    type: 'string'
  },
  not_ends_with: {
    ...DIMENSION_OPERATORS.not_ends_with,
    type: 'string'
  },
  matches_regex: {
    ...DIMENSION_OPERATORS.matches_regex,
    type: 'string'
  },
  not_matches_regex: {
    ...DIMENSION_OPERATORS.not_matches_regex,
    type: 'string'
  },
  is: {
    ...OPTION_OPERATORS.is,
    type: 'single_select'
  },
  not_is: {
    ...OPTION_OPERATORS.not_is,
    type: 'single_select'
  },
  one_of: {
    ...OPTION_OPERATORS.one_of,
    type: 'multi_select'
  },
  not_one_of: {
    ...OPTION_OPERATORS.not_one_of,
    type: 'multi_select'
  },
  is_null: {
    ...DIMENSION_OPERATORS.is_null,
    type: null
  },
  is_not_null: {
    ...DIMENSION_OPERATORS.is_not_null,
    type: null
  }
}

export const OPERATOR_OPTIONS = {
  [DIMENSION_OPERATORS_WITH_TYPES.is.id]: DIMENSION_OPERATORS_WITH_TYPES.is,
  [DIMENSION_OPERATORS_WITH_TYPES.not_is.id]: DIMENSION_OPERATORS_WITH_TYPES.not_is,
  [DIMENSION_OPERATORS_WITH_TYPES.one_of.id]: DIMENSION_OPERATORS_WITH_TYPES.one_of,
  [DIMENSION_OPERATORS_WITH_TYPES.not_one_of.id]: DIMENSION_OPERATORS_WITH_TYPES.not_one_of,
  [DIMENSION_OPERATORS_WITH_TYPES.is_null.id]: DIMENSION_OPERATORS_WITH_TYPES.is_null,
  [DIMENSION_OPERATORS_WITH_TYPES.is_not_null.id]: DIMENSION_OPERATORS_WITH_TYPES.is_not_null,
  [DIMENSION_OPERATORS_WITH_TYPES.contains.id]: DIMENSION_OPERATORS_WITH_TYPES.contains,
  [DIMENSION_OPERATORS_WITH_TYPES.not_contains.id]: DIMENSION_OPERATORS_WITH_TYPES.not_contains,
  [DIMENSION_OPERATORS_WITH_TYPES.equals.id]: DIMENSION_OPERATORS_WITH_TYPES.equals,
  [DIMENSION_OPERATORS_WITH_TYPES.not_equals.id]: DIMENSION_OPERATORS_WITH_TYPES.not_equals,
  [DIMENSION_OPERATORS_WITH_TYPES.matches_regex.id]: DIMENSION_OPERATORS_WITH_TYPES.matches_regex,
  [DIMENSION_OPERATORS_WITH_TYPES.not_matches_regex.id]: DIMENSION_OPERATORS_WITH_TYPES.not_matches_regex
}
